import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { WalletTypeEnum } from '@ps/isp-web-services-types';
import { floor } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { getIsWalletBonusCash } from '@swf/core/models/sports/extraWallets/utils/getIsWallet';
import { dispatch, select } from '@swf/core/store';

import { getWalletFieldName } from '../utils/getWalletFieldName';

const usePromotionPicker = (
  opportunity: SingleOpportunity | MultipleOpportunity,
  fieldName: string,
  numberOfLines: number,
  walletId?: string
) => {
  const { mutators, getFieldState } = useForm<FormData>();
  const extraWallets = useSelector(select.extraWallets.getAllEnabledWallets);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const extraWallet = extraWallets.find((wallet) => wallet.id === walletId);
  const selectedPromotion = useSelector(select.betSlip.selectedPromotion);

  const freeBetState = getFieldState(`${fieldName}.bonusCash` as keyof FormData);
  const bonusCashState = getFieldState(`${fieldName}.freeBet` as keyof FormData);
  const hasOpportunityActivePromo = freeBetState?.length || bonusCashState?.length;

  const promotionRedeemable = getIsWalletBonusCash(extraWallet);

  const promotionName = getWalletFieldName(WalletTypeEnum.BONUS_CASH, promotionRedeemable);

  const promotionFieldName = `${fieldName}.${promotionName}`;

  const isEligibleForPromotion =
    Boolean(opportunity.id) && getIsAvailableOpportunity(opportunity) && opportunity.hasBonusMoney;

  const promotionStake = extraWallet
    ? String(floor(extraWallet.amount / numberOfLines, 2))
    : undefined;

  const setPromotionFieldValue = () => {
    if (promotionFieldName && promotionStake && walletId) {
      mutators.setFieldValue(`${fieldName}.stakePerLine`, promotionStake);
      mutators.setFieldValue(promotionFieldName, [walletId]);
    }
  };

  const clearPromotionFieldValue = () => {
    mutators.clearFieldValue(`${fieldName}.stakePerLine`);
    mutators.clearFieldValue(`${fieldName}.bonusCash`);
    mutators.clearFieldValue(`${fieldName}.freeBet`);
  };

  /**
   * If the promotion stake changes ( eachWay selected or wallet change )
   * and the currently selected promotion is the current wallet - update the stakePerLine field
   */
  useEffect(() => {
    if (hasOpportunityActivePromo && promotionStake && selectedPromotion === walletId) {
      mutators.setFieldValue(`${fieldName}.stakePerLine`, promotionStake);
    }
  }, [fieldName, mutators, promotionStake, hasOpportunityActivePromo, selectedPromotion, walletId]);

  // If the opportunity is no longer available, clear the extra wallet field
  useEffect(() => {
    if (!getIsAvailableOpportunity(opportunity)) {
      mutators.clearFieldValue(promotionFieldName);
    }
  }, [promotionFieldName, mutators, opportunity]);

  // Clear the extra wallet field if the opportunity is no longer eligible for the extra wallet
  useEffect(() => {
    if (!isEligibleForPromotion) {
      mutators.clearFieldValue(promotionFieldName);
    }
  }, [promotionFieldName, isEligibleForPromotion, mutators]);

  useEffect(() => {
    if (!isClientAuthenticated) dispatch.betSlip.promotionPickerToggled(undefined);
  }, [isClientAuthenticated]);

  return {
    setPromotionFieldValue,
    clearPromotionFieldValue
  };
};

export default usePromotionPicker;
